import React from 'react';
import * as Yup from 'yup';
import {
  Button,
  Container,
  FormRadioButtons,
  FormTextField,
  Message, StatusMessage, TextualLink,
} from '@hvcgroep/design-system';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import Helmet from 'react-helmet';
import { useDispatch } from 'react-redux';
import ContractSwitcherHeader from '../../containers/ContractSwitcherHeader';
import useContract from '../../hooks/useContract';
import validateIBAN from '../../helpers/validateIBAN';
import formatIBAN from '../../helpers/formatIBAN';
import getPaymentMethod from '../../helpers/getPaymentMethod';
import { api, usePostChangeInvoiceCycleMutation, usePostChangePaymentMethodMutation } from '../../store/api-generated';

import './style.scss';
import SkeletonLoader from '../../components/SkeletonLoader';
import getEntries from '../../helpers/getEntries';
import useTypedSelector from "../../helpers/useTypedSelector";

const paymentMethodMapping = {
  FIRST: '2FIR',
  RECURRING: '3REC',
  NOT_ENROLLED: '1NOT',
  IDEAL: '0IDL',
} as const;

const ChangePaymentDetails: React.FC = () => {
  const { t } = useTranslation();
  const heading = t('pages.changePaymentDetails.title');
  const siteTitle = t('general.siteTitle');

  const {
    selectedContract, selectedAccount, selectedCustomer,
  } = useContract();

  const validationSchema = Yup.object().shape({
    iban: Yup.string().required(t('pages.changePaymentDetails.error.required')).transform(formatIBAN).test('iban', t('pages.changePaymentDetails.error.invalidIban'), validateIBAN),
  });

  const billingCycles = [1, 8, 15, 22];
  const currentDate = new Date().getDate();
  const billingCycle = selectedAccount?.financial?.billingCycle ?? -1;
  const hasReceivedInvoice = billingCycle <= currentDate;
  const [operationStatus, setOperationStatus] = React.useState<'success' | 'error' | 'loading' | null>(null);
  const dispatch = useDispatch();

  const status = selectedContract?.status;
  const shouldShowBillingCycleForm = status !== 'PENDING_FINAL';
  const isEditable = status !== 'FINALIZED' && status !== 'TERMINATED';

  const [changePaymentMethod, {
    isLoading: isLoadingChangePaymentMethod,
    isError: isErrorChangePaymentMethod,
  }] = usePostChangePaymentMethodMutation();

  const [changeInvoiceCycle, {
    isLoading: isLoadingChangeInvoiceCycle,
    isError: isErrorChangePaymentCycle,
  }] = usePostChangeInvoiceCycleMutation();

  React.useEffect(() => {
    if (isLoadingChangePaymentMethod || isLoadingChangeInvoiceCycle) {
      setOperationStatus('loading');

      return;
    }

    if (isErrorChangePaymentMethod || isErrorChangePaymentCycle) {
      setOperationStatus('error');

      return;
    }

    setOperationStatus(null);
  }, [isLoadingChangePaymentMethod, isLoadingChangeInvoiceCycle, isErrorChangePaymentMethod, isErrorChangePaymentCycle]);

  if (!selectedAccount || !selectedContract || !selectedCustomer) {
    return (
      <Container fluid>
        <Helmet><title>{`${heading} - ${siteTitle}`}</title></Helmet>

        <SkeletonLoader type="general" />
      </Container>
    );
  }

  const initialValues = {
    iban: selectedAccount.financial?.bankAccountNumber && formatIBAN(selectedAccount.financial.bankAccountNumber),
    payment_method: getPaymentMethod(selectedAccount),
    billing_cycle: String(selectedAccount.financial?.billingCycle),
  };

  const onSubmit = (values: typeof initialValues) => {
    const changedValues: (keyof typeof initialValues)[] = [];
    const cleanedIban = (values.iban ?? '').replace(/\s/g, '');

    getEntries(initialValues).forEach(([key]) => {
      if (String(values[key]) !== String(initialValues[key])) {
        changedValues.push(key);
      }
    });

    const operationsToExecute: Promise<any>[] = [];
    if (changedValues.includes('payment_method') || changedValues.includes('iban')) {
      operationsToExecute.push(changePaymentMethod({
        paymentMethod: {
          alternateId: String(selectedCustomer.alternateId),
          accountId: String(selectedAccount.id),
          nieuweIban: cleanedIban,
          nieuweBetaalwijze: paymentMethodMapping[String(values.payment_method) as keyof typeof paymentMethodMapping],
        },
      }));
    }

    if (changedValues.includes('billing_cycle')) {
      operationsToExecute.push(changeInvoiceCycle({
        invoiceCycle: {
          alternateId: String(selectedCustomer.alternateId),
          accountId: String(selectedAccount.id),
          nieuweInvoiceCyle: String(values.billing_cycle),
        },
      }));
    }

    Promise.all(operationsToExecute).then((results) => {
      const isError = results.some((result) => !!result.error);

      if (isError) {
        setOperationStatus('error');

        return;
      }

      setOperationStatus('success');

      dispatch(api.util.updateQueryData('getCustomerDetails', {}, (draft) => {
        const customer = draft.find((c) => c.alternateId === selectedCustomer.alternateId);
        const account = customer?.accounts?.find((a) => a.id === selectedAccount.id);

        if (account?.financial) {
          account.financial.bankAccountNumber = cleanedIban;
          account.financial.billingCycle = parseInt(values.billing_cycle, 10);
          account.financial.status = values.payment_method as string;
        }
      }));
    }).catch(() => {
      setOperationStatus('error');
    });
  };

  return (
    <Container fluid>
      {operationStatus === 'success' && (
        <Message
          type="status"
          actionButton={(
            <TextualLink
              onClick={() => {
                setOperationStatus(null);
              }}
            >
              {t('messages.closeMessage')}
            </TextualLink>
        )}
        >
          {t('pages.changePaymentDetails.messages.success')}
        </Message>
      )}
      {operationStatus === 'error' && (
        <Message
          type="error"
        >
          {t('pages.changePaymentDetails.messages.error')}
        </Message>
      )}
      <Helmet><title>{`${heading} - ${siteTitle}`}</title></Helmet>

      {selectedAccount && (
        <div>
          <ContractSwitcherHeader heading={heading} />
          <div className="row">
            <div className="col-md-6 mo-changePaymentForm">
              {/* @TODO payment method should be updated with the option of manual payment for customers that have this option selected already */}
              {/* @TODO the submit function of the form should be added still */}
              <Formik
                validationSchema={validationSchema}
                initialValues={initialValues}
                onSubmit={onSubmit}
                validateOnChange={false}
                validateOnBlur={false}
                enableReinitialize
              >
                <Form>
                  { isEditable && (
                    <>
                      <FormTextField
                        className="AddressSelectionFields__input AddressSelectionFields__input--postalCode"
                        name="iban"
                        label={t('pages.changePaymentDetails.ibanLabel')}
                        placeholder={t('pages.changePaymentDetails.ibanPlaceholder')}
                        isLiveValidationDisabled
                      />

                      <FormRadioButtons
                        name="payment_method"
                        label={t('pages.changePaymentDetails.paymentMethodLabel')}
                        isLiveValidationDisabled
                        options={[{
                          key: 'RECURRING',
                          value: t('pages.changePaymentDetails.automatedPayment'),
                        },
                        {
                          key: 'IDEAL',
                          value: t('pages.changePaymentDetails.ideal'),
                        },
                        ...getPaymentMethod(selectedAccount) === 'NOT_ENROLLED' ? [{
                          key: 'NOT_ENROLLED',
                          value: t('pages.changePaymentDetails.manualPayment'),
                        }] : [],
                        ]}
                      />
                    </>
                  )}

                  {shouldShowBillingCycleForm && isEditable && (
                    <>
                      <FormRadioButtons
                        name="billing_cycle"
                        label={t('pages.changePaymentDetails.paymentCollectionLabel')}
                        isLiveValidationDisabled
                        options={[
                          {
                            key: '1',
                            value: t('pages.changePaymentDetails.billingCycle1'),
                          },
                          {
                            key: '8',
                            value: t('pages.changePaymentDetails.billingCycle8'),

                          },
                          {
                            key: '15',
                            value: t('pages.changePaymentDetails.billingCycle15'),
                          },
                          {
                            key: '22',
                            value: t('pages.changePaymentDetails.billingCycle22'),
                          }]}
                      />

                      <p className="change-payment-details__info">
                        {t('pages.changePaymentDetails.guidingText')}
                      </p>
                    </>
                  )}

                  {(!shouldShowBillingCycleForm || !isEditable) && (
                    <>
                      {/* IBAN */}
                      {!isEditable && (
                        <fieldset className="notEditable">
                          <div>
                            <legend className="u-margin--bottom--4">{t('pages.changePaymentDetails.ibanLabel')}</legend>
                          </div>
                          <Message type="warning">
                            {t('pages.changePaymentDetails.notEditable.iban')}
                          </Message>
                        </fieldset>
                      )}

                      {/* BETALINGSMETHODE */}
                      {!isEditable && (
                        <fieldset className="notEditable">
                          <div>
                            <legend className="u-margin--bottom--4">{t('pages.changePaymentDetails.paymentMethodLabel')}</legend>
                          </div>
                          <Message type="warning">
                            {t('pages.changePaymentDetails.notEditable.paymentMethod')}
                          </Message>
                        </fieldset>
                      )}

                      {/* INCASSOMOMENT */}
                      {(!shouldShowBillingCycleForm || !isEditable) && (
                        <fieldset className="notEditable">
                          <div>
                            <legend className="u-margin--bottom--4">{t('pages.changePaymentDetails.paymentCollectionLabel')}</legend>
                          </div>
                          <Message type="warning">
                            {t('pages.changePaymentDetails.notEditable.collectionMoment')}
                          </Message>
                        </fieldset>
                      )}
                    </>
                  )}

                  <div>
                    { isEditable && (
                      <Button
                        type="primary"
                        htmlType="submit"
                        variation="center"
                        isLoading={operationStatus === 'loading'}
                      >
                        {t('pages.changePaymentDetails.submitLabel')}
                      </Button>
                    )}
                  </div>


                </Form>

              </Formik>
            </div>
          </div>

        </div>

      )}

    </Container>
  );
};

export default ChangePaymentDetails;
